import React, { useEffect, useState } from 'react';
import { NestedMenu, PbiMenuItem, PbiMenuPopover, ReportLinkButton } from './PBIReport.styles';
import { useAuthStore } from 'src/context/AuthContext';
import { usePbiReportPagesStore } from 'src/context/PbiReportsContext';
import { useQueryParamsHandler } from 'odfjell-query-manager';
import { Link } from 'react-router-dom';
import useAppFeature from 'src/app/useAppFeature';
import { AppFeatureEnum } from 'src/enums/appFeatureEnum';

enum ReportListType {
    Tankers = 'Tankers',
    SM = 'SM',
}

const PBIMenu: React.FC = () => {
    const { pbiAuthorization } = useAuthStore();
    const { setReportPages } = usePbiReportPagesStore();
    const { isFeatureEnabled } = useAppFeature();
    const { getQueryParamValueFromUrl } = useQueryParamsHandler();
    const [reportKeyList, setReportKeyList] = useState<string[] | null>(null);
    const [reportTypeList, setReportTypeList] = useState<ReportListType[]>([]);
    const [hoveredReportListType, setHoveredReportListType] = useState<ReportListType | null>(null);

    useEffect(() => {
        if (!pbiAuthorization?.reports) return;
        const smReportsEnabled = isFeatureEnabled({ featureEnum: AppFeatureEnum.SmReports, date: new Date() });
        if (hoveredReportListType) {
            setReportKeyList(
                hoveredReportListType === ReportListType.SM
                    ? pbiAuthorization.reports.filter((r) => r.smRestricted && smReportsEnabled).map((r) => r.reportName)
                    : pbiAuthorization.reports.filter((r) => !r.smRestricted).map((r) => r.reportName)
            );
        } else if (reportTypeList.length === 1 && reportTypeList[0] === ReportListType.Tankers) {
            setReportKeyList(pbiAuthorization.reports.filter((r) => !r.smRestricted).map((r) => r.reportName));
        }
    }, [reportTypeList, hoveredReportListType, pbiAuthorization]);

    useEffect(() => {
        setReportTypeList(
            Object.values(ReportListType).filter(
                (type) =>
                    (type === ReportListType.Tankers || isFeatureEnabled({ featureEnum: AppFeatureEnum.SmReports, date: new Date() })) &&
                    pbiAuthorization?.reports.some((r) => (type === ReportListType.SM ? r.smRestricted : !r.smRestricted))
            )
        );
    }, [pbiAuthorization]);

    // The Link carries the report params in its target. Calling triggerQueryParamsUpdate here as well races the
    // navigation: the outgoing page's effects rewrite the URL and drop reportName.
    const getReportSearch = (reportKey: string): string => {
        const params = new URLSearchParams();
        const imoNumber = getQueryParamValueFromUrl('imoNumber');
        if (imoNumber) params.set('imoNumber', imoNumber);
        if (hoveredReportListType) params.set('dashboard', hoveredReportListType);
        params.set('reportName', reportKey);
        return `?${params.toString()}`;
    };

    return (
        <PbiMenuPopover>
            {reportTypeList.length > 1
                ? reportTypeList
                      .filter(
                          (type) =>
                              (type === ReportListType.Tankers || isFeatureEnabled({ featureEnum: AppFeatureEnum.SmReports, date: new Date() })) &&
                              pbiAuthorization?.reports.some((r) => (type === ReportListType.SM ? r.smRestricted : !r.smRestricted))
                      )
                      .map((type) => (
                          <PbiMenuItem
                              key={type}
                              onMouseEnter={() => setHoveredReportListType(type)}
                              onMouseLeave={() => setHoveredReportListType(null)}
                          >
                              <ReportLinkButton isSelected={getQueryParamValueFromUrl('dashboard') === type}>{type}</ReportLinkButton>
                              {hoveredReportListType === type && (
                                  <NestedMenu>
                                      {reportKeyList?.map((reportKey) => (
                                          <PbiMenuItem key={reportKey}>
                                              <Link
                                                  onClick={() => setReportPages([])}
                                                  to={{
                                                      pathname: 'dashboards',
                                                      search: getReportSearch(reportKey),
                                                  }}
                                              >
                                                  <ReportLinkButton isSelected={getQueryParamValueFromUrl('reportName') === reportKey}>
                                                      {pbiAuthorization.reports.find((r) => r.reportName === reportKey).reportDisplayName ??
                                                          reportKey}
                                                  </ReportLinkButton>
                                              </Link>
                                          </PbiMenuItem>
                                      ))}
                                  </NestedMenu>
                              )}
                          </PbiMenuItem>
                      ))
                : reportKeyList?.map((reportKey) => (
                      <PbiMenuItem key={reportKey}>
                          <Link
                              onClick={() => setReportPages([])}
                              to={{
                                  pathname: 'dashboards',
                                  search: getReportSearch(reportKey),
                              }}
                          >
                              <ReportLinkButton isSelected={getQueryParamValueFromUrl('reportName') === reportKey}>
                                  {pbiAuthorization.reports.find((r) => r.reportName === reportKey).reportDisplayName ?? reportKey}
                              </ReportLinkButton>
                          </Link>
                      </PbiMenuItem>
                  ))}
        </PbiMenuPopover>
    );
};

export default PBIMenu;
